/**
 * Create User Dialog Component
 * Handles creation of new users with optional email verification
 *
 * KEY FEATURE: Email Verification Toggle
 * - Local Employee (default): Account active immediately, welcome email with password
 * - Remote Employee: Account inactive until email verified, verification link sent
 *
 * @module Features/UserManagement/Dialogs
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useCreateUserMutation } from '../Service/usersApi';
import {
  createUserValidationSchema,
  CreateUserFormData,
  sanitizeCreateUserData,
} from '../Validation/userValidationSchema';
import { closeCreateDialog } from '../slices/userManagementSlice';
import { CreateUserRequest, UserRole } from '../Types';
import { useSnackbar } from 'notistack';

/**
 * Create User Dialog Component
 */
const CreateUserDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // React Hook Form with Yup validation
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    resolver: yupResolver(createUserValidationSchema) as any,
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      roles: ['Clerk'],
      initialPassword: '',
      sendWelcomeEmail: true,
      requireEmailVerification: false, // Default: local employee
    },
  });

  // Watch email verification toggle
  const requireEmailVerification = watch('requireEmailVerification');

  const onSubmit = async (data: CreateUserFormData) => {
    try {
      setSubmitError(null);
      const payload: CreateUserRequest = sanitizeCreateUserData(data);

      const response = await createUser(payload).unwrap();

      // Show temp password if local flow
      if (response.temporaryPassword) {
        setTempPassword(response.temporaryPassword);
        setShowPassword(true);
        enqueueSnackbar('Użytkownik utworzony pomyślnie', { variant: 'success' });
        console.log('✅ Success:', response.message);
      } else {
        // Remote flow - no password
        enqueueSnackbar('Użytkownik utworzony pomyślnie, link weryfikacyjny wysłany', { variant: 'success' });
        console.log('✅ Success:', response.message);
        handleClose();
      }
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setSubmitError(message);
      enqueueSnackbar(message, { variant: 'error' });
      console.error('❌ Error:', message, error);
    }
  };

  const handleClose = () => {
    setTempPassword(null);
    setShowPassword(false);
    dispatch(closeCreateDialog());
  };

  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      console.log('📋 Password copied to clipboard');
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 600 }}>Utwórz nowego użytkownika</DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          {/* Email field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email"
                type="email"
                placeholder="john@agency.com"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoFocus
              />
            )}
          />

          {/* Name fields */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Imię"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nazwisko"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Stack>

          {/* Roles selection */}
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.roles}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...field}
                  multiple
                  label="Rola"
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value={UserRole.Admin}>{UserRole.Admin}</MenuItem>
                  <MenuItem value={UserRole.Clerk}>{UserRole.Clerk}</MenuItem>
                </Select>
                {errors.roles && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.roles.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {/* Optional password field */}
          <Controller
            name="initialPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Hasło (opcjonalne)"
                type={showPassword ? 'text' : 'password'}
                placeholder="Zostaw puste aby automatycznie wygenerować hasło"
                error={!!errors.initialPassword}
                helperText={
                  errors.initialPassword?.message ||
                  'Jeśli podano: min. 8 znaków, w tym wielkie i małe litery, cyfry i znaki specjalne'
                }
              />
            )}
          />

          {/* Send welcome email */}
          <Controller
            name="sendWelcomeEmail"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Wyślij e-mail powitalny"
              />
            )}
          />

          {/* ⭐ EMAIL VERIFICATION TOGGLE - KEY FEATURE */}
          <Controller
            name="requireEmailVerification"
            control={control}
            render={({ field }) => (
              <Box>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'info.lighter',
                    border: '1px solid',
                    borderColor: 'info.light',
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Wymagaj weryfikacji adresu e-mail
                      </Typography>
                    }
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      ml: 4,
                      color: 'textSecondary',
                    }}
                  >
                    ℹ️ Jeśli włączone: Konto nieaktywne do czasu weryfikacji adresu e-mail. Link
                    weryfikacyjny ważny przez 24 godziny. Używaj w przypadku pracowników zdalnych
                    lub stanowisk o wysokim poziomie bezpieczeństwa.
                  </Typography>

                  {/* Preview alert */}
                  {requireEmailVerification ? (
                    <Alert severity="info" sx={{ mt: 2, mb: 0 }} icon={<Typography>🔗</Typography>}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Przepływ pracowników zdalnych:
                      </Typography>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        <li>
                          Konto będzie<strong>nieaktywne</strong>
                        </li>
                        <li>Wiadomość weryfikacyjna zostanie wysłana</li>
                        <li>Użytkownik musi kliknąć link aktywacyjny</li>
                        <li>
                          Token wygasa po <strong>24 godzinach</strong>
                        </li>
                      </ul>
                    </Alert>
                  ) : (
                    <Alert
                      severity="success"
                      sx={{ mt: 2, mb: 0 }}
                      icon={<Typography>✓</Typography>}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Przepływ pracowników lokalnych (domyślny):
                      </Typography>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        <li>
                          Konto będzie <strong>aktywne natychmiast</strong>
                        </li>
                        <li>Wiadomość powitalna z tymczasowym hasłem</li>
                        <li>Użytkownik musi zmienić hasło przy pierwszym logowaniu</li>
                      </ul>
                    </Alert>
                  )}
                </Paper>
              </Box>
            )}
          />
        </Stack>
      </DialogContent>

      {/* Password display (shown after successful creation) */}
      {tempPassword && (
        <Box sx={{ px: 3, py: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #eee' }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Tymczasowe hasło wygenerowane:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                mt: 1,
                wordBreak: 'break-all',
              }}
            >
              {tempPassword}
            </Typography>
          </Alert>
          <Button fullWidth variant="outlined" size="small" onClick={copyPassword} sx={{ mt: 1 }}>
            Kopiuj hasło
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'textSecondary' }}>
            Udostępnij to hasło pracownikowi za pomocą bezpiecznego kanału. Będzie on zobowiązany do
            zmiany hasła przy pierwszym logowaniu.
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Tworzenie...' : 'Utwórz użytkownika'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const extractErrorMessage = (error: any): string => {
  const messageFromData = error?.data?.message as string | undefined;
  if (messageFromData) {
    return messageFromData;
  }

  const validationDetails = error?.data?.details as Record<string, string[]> | undefined;
  if (validationDetails) {
    const firstError = Object.values(validationDetails)?.[0]?.[0];
    if (firstError) {
      return firstError;
    }
  }

  return 'Failed to create user';
};

export default CreateUserDialog;
